import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import {
  answerChallenge,
  login,
  needsConfirmation,
  safeReturnTo,
  type Challenge,
  type SignInResult,
} from '../auth'
import { ConfirmCode, Field, PASSWORD_HINT } from '../components/AuthParts'
import { ProblemBanner } from '../components/Problem'

type Step =
  | { kind: 'credentials' }
  | { kind: 'challenge'; challenge: Challenge; session: string }
  | { kind: 'confirm' }

/**
 * Email and password sign-in, on this site rather than on Cognito's (spec §8.3). Also the
 * page for confirming a recent sign-in (`?reauth=1`), which account deletion needs.
 */
export default function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [parameters] = useSearchParams()
  const navigate = useNavigate()
  const returnTo = safeReturnTo(parameters.get('returnTo'))
  const reauth = parameters.get('reauth') === '1'
  const justReset = parameters.get('reset') === '1'

  const [step, setStep] = useState<Step>({ kind: 'credentials' })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const settle = (result: SignInResult) => {
    if (result.state === 'signed-in') {
      onSignedIn()
      navigate(returnTo, { replace: true })
    } else {
      setCode('')
      setStep({ kind: 'challenge', challenge: result.challenge, session: result.session })
    }
  }

  const run = (work: Promise<void>) => {
    setBusy(true)
    setError(null)
    work.catch(setError).finally(() => setBusy(false))
  }

  const submitCredentials = (event: React.FormEvent) => {
    event.preventDefault()
    run(
      login(email.trim(), password)
        .then(settle)
        .catch((failure: unknown) => {
          // Right password, address not confirmed yet: carry on to the code step.
          if (needsConfirmation(failure)) setStep({ kind: 'confirm' })
          else throw failure
        }),
    )
  }

  if (step.kind === 'confirm') {
    return (
      <div className="auth">
        <ConfirmCode
          email={email.trim()}
          onConfirmed={() => login(email.trim(), password).then(settle)}
        />
      </div>
    )
  }

  if (step.kind === 'challenge') {
    const mfa = step.challenge === 'SOFTWARE_TOKEN_MFA'
    const submitChallenge = (event: React.FormEvent) => {
      event.preventDefault()
      run(
        answerChallenge(
          email.trim(),
          step.challenge,
          step.session,
          mfa ? { code: code.trim() } : { newPassword },
        ).then(settle),
      )
    }
    return (
      <div className="auth">
        <form className="auth-form" onSubmit={submitChallenge}>
          <h1>{mfa ? 'Two-step verification' : 'Choose a new password'}</h1>
          <p>
            {mfa
              ? 'Enter the code from your authenticator app.'
              : 'Your password was set for you. Choose your own to continue.'}
          </p>
          <ProblemBanner error={error} />
          {mfa ? (
            <Field
              label="Authenticator code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              autoFocus
            />
          ) : (
            <Field
              label="New password"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              hint={PASSWORD_HINT}
              required
              autoFocus
            />
          )}
          <div className="row">
            <button className="primary" type="submit" disabled={busy}>
              {busy ? 'Checking…' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="auth">
      <form className="auth-form" onSubmit={submitCredentials}>
        <h1>{reauth ? 'Confirm it is you' : 'Log in'}</h1>
        {reauth ? (
          <p>For your security, enter your password again to continue.</p>
        ) : null}
        {justReset ? (
          <p className="notice good" role="status">
            Your password has been changed. Log in with the new one.
          </p>
        ) : null}
        <ProblemBanner error={error} />
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="username"
          required
          autoFocus
        />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />
        <div className="row">
          <button className="primary" type="submit" disabled={busy}>
            {busy ? 'Logging in…' : 'Log in'}
          </button>
        </div>
        <p className="muted">
          <Link to="/forgot">Forgot your password?</Link>
          {reauth ? null : (
            <>
              {' · '}
              <Link to={{ pathname: '/signup', search: parameters.toString() }}>
                Create an account
              </Link>
            </>
          )}
        </p>
      </form>
    </div>
  )
}
